import React from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
} from "@mui/material";
import PictureAsPdfRoundedIcon from "@mui/icons-material/PictureAsPdfRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import FileDownloadRoundedIcon from "@mui/icons-material/FileDownloadRounded";
import PlaceRoundedIcon from "@mui/icons-material/PlaceRounded";
import { motion } from "framer-motion";
import { fig } from "../../theme";
import SwanLogo from "../../assets/logos/swansolutions_logo.png";
import { useSiteContent } from "../../services/content";
import { downloadImage } from "../../services/media";

// The four anonymized customer finance white papers, authored in the ADS project
// and registered against the swank12 site pages in AIOS. Ordered to match the
// numbered source PDFs (01–04). These are curated, rarely-changing documents, so
// they live here directly rather than behind a runtime API.
const WHITE_PAPERS = [
  {
    title: "The Complete Finance System",
    region: "Florida",
    blurb:
      "A Florida K-12 district (~15 schools, ~13,000 students) unified Skyward labor, PrimeroEdge POS, and AI invoice scanning into one real-time P&L with FIG Finance — five systems became one.",
    url: "https://media.swancreate.com/f/01928b25-1d8d-4cfb-a42a-673d013b4242",
    file: "01-complete-finance-system.pdf",
    accent: `linear-gradient(135deg, ${fig.tealDark} 0%, ${fig.teal} 100%)`,
  },
  {
    title: "Negative Balance Management",
    region: "Tennessee",
    blurb:
      "A Tennessee district (~20 schools) cut meal debt by removing payment friction — instant phone payment (Venmo, Apple Pay, no portal login) plus an integrated Angel Fund that paid off an entire school.",
    url: "https://media.swancreate.com/f/ba6aa2b7-7c89-4a4e-a98d-69797eebd383",
    file: "02-negative-balance-management.pdf",
    accent: `linear-gradient(135deg, ${fig.teal} 0%, ${fig.green} 100%)`,
  },
  {
    title: "Invoice Review & Bid Management",
    region: "South Carolina",
    blurb:
      "A South Carolina district (~18 schools) checks every invoice line against awarded bid pricing with FIG Finance, surfacing five- and six-figure variance to enforce vendor contracts.",
    url: "https://media.swancreate.com/f/c7c50232-15ab-4e10-aeec-f154b7bffdd9",
    file: "03-invoice-review-bid-management.pdf",
    accent: `linear-gradient(135deg, ${fig.teal} 0%, ${fig.orange} 100%)`,
  },
  {
    title: "Complete KPI Management",
    region: "Florida",
    blurb:
      "A 65-school Florida district replaced a legacy system and spreadsheets with FIG Finance — real-time KPIs, MPLH, per-MEQ economics, and automatic monthly site report cards.",
    url: "https://media.swancreate.com/f/ec83ad4f-376b-4073-9f79-7e57f6bc4d0d",
    file: "04-kpi-management.pdf",
    accent: `linear-gradient(135deg, ${fig.greenDark} 0%, ${fig.green} 100%)`,
  },
];

export default function WhitePapers() {
  const content = useSiteContent();

  return (
    <Box sx={{ bgcolor: "background.default" }}>
      {/* HERO */}
      <Box
        component={motion.section}
        initial={{ opacity: 0, y: -64 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1 }}
        sx={{
          width: "100vw",
          left: "50%",
          right: "50%",
          marginLeft: "-50vw",
          marginRight: "-50vw",
          py: { xs: 10, md: 14 },
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          color: "#fff",
          background: fig.gradientHero,
          backgroundSize: "cover",
          minHeight: { xs: "30vh", md: "34vh" },
          position: "relative",
          overflow: "hidden",
          boxShadow: "0 6px 40px 0 rgba(30,190,200,0.18)",
          mb: 8,
        }}
      >
        <Box
          component="img"
          src={SwanLogo}
          alt="Swan Solutions"
          sx={{ height: { xs: 64, md: 100 }, mb: 3, filter: "drop-shadow(0 4px 24px rgba(0,0,0,0.12))" }}
        />
        <Typography
          variant="h2"
          sx={{ fontWeight: 900, mb: 2, fontSize: { xs: "2rem", md: "3rem" }, letterSpacing: "-0.04em", textShadow: "0 3px 16px rgba(15,90,95,0.22)" }}
        >
          {content("whitepapers.hero.title", "Finance White Papers")}
        </Typography>
        <Typography
          variant="h5"
          sx={{ maxWidth: 820, mx: "auto", px: 2, color: "rgba(255,255,255,0.97)", fontWeight: 400, fontSize: { xs: "1.1rem", md: "1.2rem" } }}
        >
          {content(
            "whitepapers.hero.subtitle",
            "Real results from real K-12 districts. Each anonymized case study shows how FIG Finance turned scattered systems and spreadsheets into one clear financial picture."
          )}
        </Typography>
      </Box>

      <Container maxWidth="lg" sx={{ pb: 10 }}>
        <Grid container spacing={4}>
          {WHITE_PAPERS.map((wp, i) => (
            <Grid key={wp.url} item xs={12} sm={6} md={6} lg={3}>
              <motion.div
                style={{ height: "100%" }}
                initial={{ scale: 0.94, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ type: "spring", stiffness: 130, damping: 20, delay: i * 0.06 }}
                whileHover={{ y: -6, scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Card
                  sx={{
                    height: "100%",
                    borderRadius: 4,
                    boxShadow: "0 8px 30px rgba(30,190,200,0.12)",
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                    transition: "box-shadow 0.2s",
                    "&:hover": { boxShadow: "0 14px 40px rgba(30,190,200,0.24)" },
                  }}
                >
                  {/* Accent header with PDF mark + region */}
                  <Box
                    sx={{
                      background: wp.accent,
                      color: "#fff",
                      px: 2.5,
                      pt: 2.5,
                      pb: 3,
                      display: "flex",
                      flexDirection: "column",
                      gap: 1.5,
                      minHeight: 132,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <PictureAsPdfRoundedIcon sx={{ fontSize: 42, opacity: 0.95 }} />
                      <Chip
                        icon={<PlaceRoundedIcon sx={{ color: "#fff !important", fontSize: 16 }} />}
                        label={wp.region}
                        size="small"
                        sx={{ bgcolor: "rgba(255,255,255,0.18)", color: "#fff", fontWeight: 600, backdropFilter: "blur(2px)" }}
                      />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2, mt: "auto" }}>
                      {wp.title}
                    </Typography>
                  </Box>

                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.55 }}>
                      {wp.blurb}
                    </Typography>
                  </CardContent>

                  <CardActions sx={{ px: 2, pb: 2, pt: 0, gap: 1 }}>
                    <Button
                      component={motion.a}
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.96 }}
                      transition={{ type: "spring", stiffness: 330, damping: 16 }}
                      href={wp.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="contained"
                      startIcon={<OpenInNewRoundedIcon />}
                      sx={{ flex: 1, borderRadius: 2, textTransform: "none", fontWeight: 700 }}
                    >
                      View
                    </Button>
                    <Button
                      component={motion.button}
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.96 }}
                      transition={{ type: "spring", stiffness: 330, damping: 16 }}
                      onClick={() => downloadImage(wp.url, wp.file)}
                      variant="outlined"
                      startIcon={<FileDownloadRoundedIcon />}
                      sx={{ flex: 1, borderRadius: 2, textTransform: "none", fontWeight: 700 }}
                      aria-label={`Download ${wp.title} PDF`}
                    >
                      Download
                    </Button>
                  </CardActions>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}
